// src/cockpit4/components/ViralContentBriefLoader.tsx
// ============================================
// 爆款 Content Brief 加载器
// 在内容工厂中作为"从爆款分析引擎获取选题"的数据源
// 读取 Obsidian Content_Analysis/ 下的最新简报
// ============================================

import { useState, useEffect, useCallback } from "react";
import { Brain, FileText, Loader2, ArrowRight, ExternalLink } from "lucide-react";

interface BriefFile {
  path: string;
  date: string;
  platform: string;
  category: string;
  filename: string;
}

export interface BriefData {
  path: string;
  content: string;
  date: string;
  platform: string;
  category: string;
}

interface Props {
  onSelectBrief: (brief: BriefData) => void;
  collapsed?: boolean;
}

export function useViralBrief() {
  const [briefs, setBriefs] = useState<BriefFile[]>([]);
  const [loading, setLoading] = useState(false);

  const loadBriefs = useCallback(async () => {
    setLoading(true);
    try {
      // 列出最近 7 天的 Content_Analysis 目录
      const briefsList: BriefFile[] = [];
      for (let i = 0; i < 7; i++) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        const dateStr = d.toISOString().slice(0, 10);
        try {
          const resp = await fetch(
            `/vault-api/list?path=${encodeURIComponent(
              `10_Operations（运营层）/Content_Analysis/${dateStr}`
            )}`
          );
          if (resp.ok) {
            const data = await resp.json();
            const files = (data.files || []).filter(
              (f: string) => f.endsWith("_brief.md")
            );
            for (const f of files) {
              const parts = f.replace(".md", "").split("_");
              briefsList.push({
                path: `10_Operations（运营层）/Content_Analysis/${dateStr}/${f}`,
                date: dateStr,
                platform: parts[0] === "douyin" ? "抖音" : "小红书",
                category: parts.slice(1).join("_"),
                filename: f,
              });
            }
          }
        } catch {}
      }
      setBriefs(briefsList);
    } catch (e) {
      console.warn("加载 Content Brief 列表失败:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBriefs();
  }, [loadBriefs]);

  return { briefs, loading, reload: loadBriefs };
}

export default function ViralContentBriefLoader({ onSelectBrief, collapsed }: Props) {
  const { briefs, loading, reload } = useViralBrief();
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [loadingContent, setLoadingContent] = useState(false);

  const handleSelect = async (brief: BriefFile) => {
    setSelectedPath(brief.path);
    setLoadingContent(true);
    try {
      const resp = await fetch(
        `/vault-api/read?path=${encodeURIComponent(brief.path)}`
      );
      if (resp.ok) {
        const data = await resp.json();
        if (data.content) {
          onSelectBrief({
            path: brief.path,
            content: data.content,
            date: brief.date,
            platform: brief.platform,
            category: brief.category,
          });
        }
      }
    } catch (e) {
      console.warn("加载 Content Brief 内容失败:", e);
    } finally {
      setLoadingContent(false);
      setSelectedPath(null);
    }
  };

  if (collapsed) return null;

  return (
    <div className="rounded-xl border border-cyan-500/10 bg-cyan-500/[0.02] p-3">
      <div className="flex items-center gap-2 mb-2">
        <Brain size={14} className="text-cyan-400" />
        <span className="text-[11px] font-medium text-gray-300">
          从爆款分析引擎获取选题
        </span>
        <span className="text-[9px] text-gray-600 font-mono ml-auto">
          {briefs.length} 份简报可用
        </span>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-500 text-[11px] py-2">
          <Loader2 size={11} className="animate-spin" />
          扫描 Content_Analysis...
        </div>
      ) : briefs.length === 0 ? (
        <div className="text-[11px] text-gray-600 py-2">
          <p>暂无爆款分析简报。</p>
          <p className="text-[10px] mt-1">
            在 AI 超市 → 爆款分析引擎中执行分析后，Content Brief 将自动出现在这里。
          </p>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              window.dispatchEvent(
                new CustomEvent("xeaos-navigate", {
                  detail: { workspace: "projects" },
                })
              );
            }}
            className="inline-flex items-center gap-1 text-cyan-400 hover:text-cyan-300 text-[10px] mt-1"
          >
            去 AI 超市
            <ExternalLink size={9} />
          </a>
        </div>
      ) : (
        <div className="max-h-36 overflow-auto space-y-1">
          {briefs.map((brief, i) => (
            <button
              key={i}
              onClick={() => handleSelect(brief)}
              disabled={loadingContent && selectedPath === brief.path}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-[11px] hover:bg-white/[0.04] transition disabled:opacity-50 group"
            >
              <FileText size={12} className="text-gray-500 flex-shrink-0" />
              <span className="text-gray-300 flex-1 truncate">
                {brief.platform} · {brief.category}
              </span>
              <span className="text-[9px] text-gray-600 flex-shrink-0">
                {brief.date.slice(5)}
              </span>
              {loadingContent && selectedPath === brief.path ? (
                <Loader2 size={10} className="animate-spin text-cyan-400" />
              ) : (
                <ArrowRight size={10} className="text-gray-600" />
              )}
              {/* 查看原文：在新标签页打开 Vault 中的 Markdown 文件 */}
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  const vaultPath = encodeURIComponent(brief.path);
                  window.open(`/vault-api/raw?path=${vaultPath}`, "_blank");
                }}
                title="在 Obsidian 中查看原文"
                className="opacity-0 group-hover:opacity-100 transition text-[9px] text-violet-400 hover:text-violet-200 flex-shrink-0"
              >
                <ExternalLink size={10} />
              </span>
            </button>
          ))}
        </div>
      )}

      {briefs.length > 0 && (
        <button
          onClick={reload}
          className="text-[9px] text-gray-500 hover:text-gray-300 mt-1"
        >
          刷新列表
        </button>
      )}
    </div>
  );
}
